import { getAnalytics } from "@tm/insurance/analytics";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { TextNodeType } from "@tm/ui-widgets/components";
import { theme } from "@tm/utility/global";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ICommonPageData } from "../../pages/InsuranceCommonLanding/types";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import LandingButton from "../LandingButton/LandingButton";
import EducationHighlights from "../Modal/EducationHighlights";
import EducationProcess from "../Modal/EducationProcess";
import {
  ButtonRow,
  KnowMoreLink,
  ModalContent,
  ModalOverlay,
  StepDot,
  Stepper,
  StickyFooterSection,
} from "./EducationModal.style";

interface EducationModalProps {
  pagesData?: {
    pageID: string;
    data: ICommonPageData;
    type?: string;
  }[];
  knowMore?: {
    title?: TextNodeType | undefined;
    url?: string | undefined;
  };
  setShowEducationModal: React.Dispatch<React.SetStateAction<boolean>>;
  skipButtonHandler: () => void;
  onModalShown?: () => void;
  onNextClick?: () => void;
  onSkipClick?: () => void;
  onPageShown?: (pageId: string) => void;
}

interface BackArrowProps {
  color?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  onClick?: () => void;
}

const EducationModal = ({
  pagesData,
  knowMore,
  setShowEducationModal,
  skipButtonHandler,
  onModalShown,
  onNextClick,
  onSkipClick,
  onPageShown,
}: EducationModalProps) => {
  if (!pagesData) return null;
  const analytics = getAnalytics();
  const device = useBreakpointV2();
  const fullscreen = device === "sm";
  const [page, setPage] = React.useState(0);

  // Track modal shown on mount
  React.useEffect(() => {
    onModalShown && onModalShown();
  }, []);

  // Track page shown on page change
  React.useEffect(() => {
    if (pagesData && pagesData[page] && onPageShown) {
      onPageShown(pagesData[page].pageID);
    }
  }, [page, pagesData, onPageShown]);

  // Sticky footer only if content is overflowing and device is desktop
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  useEffect(() => {
    function checkOverflow() {
      const el = modalContentRef.current;
      if (el) {
        setIsOverflowing(el.scrollHeight > el.clientHeight);
      }
    }
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [pagesData, page, device]);

  const BackArrow: React.FC<BackArrowProps> = ({
    color = theme.greyText6,
    width = 24,
    height = 24,
    className,
    onClick,
  }) => (
    <svg
      width={width}
      height={height}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        marginRight: "16px",
      }}
      viewBox="0 0 24 24"
    >
      <path
        d="m12 20-8-8 8-8 1.425 1.4-5.6 5.6H20v2H7.825l5.6 5.6L12 20Z"
        fill={color}
      />
    </svg>
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    if (device === "lg") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
    return;
  }, [device]);

  const handleNext = () => {
    onNextClick && onNextClick();
    if (page < pagesData.length - 1) {
      setPage(page + 1);
    } else {
      skipButtonHandler();
    }
  };

  const handleSkip = () => {
    onSkipClick && onSkipClick();
    skipButtonHandler();
  };

  const currentPage = pagesData[page];
  let ModalContentComponent = null;
  if (
    currentPage?.pageID === "HOW_IT_WORKS" ||
    currentPage?.pageID === "PROCESS_STEPS"
  ) {
    ModalContentComponent = <EducationProcess backendData={currentPage} />;
  } else if (currentPage?.pageID === "HIGHLIGHTS") {
    ModalContentComponent = (
      <EducationHighlights backendData={currentPage as any} />
    );
  }

  return createPortal(
    <ModalOverlay
      fullscreen={fullscreen}
      onClick={() => setShowEducationModal(false)}
    >
      <ModalContent
        ref={modalContentRef}
        fullscreen={fullscreen}
        onClick={(e) => e.stopPropagation()}
      >
        {device === "sm" && (
          <FlexComp
            justifyContent="flex-start"
            alignItems="center"
            margin="0 0 16px 0"
          >
            <BackArrow
              color={theme.greyText6}
              onClick={() => setShowEducationModal(false)}
            />
          </FlexComp>
        )}
        {ModalContentComponent}
        <StickyFooterSection $isDesktop={device === "lg" && isOverflowing}>
          <KnowMoreLink
            href={knowMore?.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedText value={knowMore?.title} />
          </KnowMoreLink>
          <Stepper>
            {pagesData.map((_, idx) => (
              <StepDot key={idx} active={idx === page} />
            ))}
          </Stepper>
          <ButtonRow>
            <LandingButton
              customTitle="SKIP"
              onClick={handleSkip}
              padding="0px 40px"
              customWidth="49%"
            />
            <LandingButton
              customTitle="NEXT"
              onClick={handleNext}
              padding="0px 40px"
              customWidth="49%"
            />
          </ButtonRow>
        </StickyFooterSection>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default EducationModal;
